import { Coding, Condition } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  getConditionCategory,
  getConditionHccCode,
  getEndTime,
  getHccCoditionsForSidePanel,
  getOnsetTime,
  isChronicCondition,
  isHccCondition,
} from "../../../shared-components/fhir-sections/conditions/table-data";
import {
  getConditionDate,
  getIcd10PrefixCode,
  groupResourcesByCode,
} from "../../table-transforms/series-utils";
import icd10PrefixLookup from "./icd10-categories.json";
import { ICD_10_SYSTEM } from "../../components/coding-badge";

dayjs.extend(utc);

export type ConditionRowData = {
  id: string;
  condition: string;
  hasEncounterDiagnosis: boolean;
  code: string;
  codings: Coding[];
  firstSeen: string;
  lastSeen: string;
  originalData: Smart<Condition>;
  isChronic: boolean;
  isHcc: boolean;
  conditions?: Smart<Condition>[];
  patientDob?: string;
};

export function getConditionRows({ bundle }: { bundle: FhirBundleSdk }): ConditionRowData[] {
  const conditions = bundle.getConditions();
  const patient = bundle.getPatients()[0];
  const patientDob = patient?.birthDate ? dayjs.utc(patient.birthDate).format(ISO_DATE) : undefined;

  // Group conditions by ICD-10 code using series utilities
  const conditionSeries = groupResourcesByCode<Smart<Condition>>(
    conditions,
    getIcd10PrefixCode,
    getConditionDate,
    "Condition"
  );

  // Map directly from series to row data
  return conditionSeries.map(series => {
    const mostRecentCondition = series.resources[series.resources.length - 1];
    const firstCondition = series.resources[0];

    if (!mostRecentCondition || !firstCondition) {
      throw new Error("Series must have at least one resource");
    }

    const groupIcd10Code = getIcd10PrefixCode(mostRecentCondition);
    const groupIcd10Display = icd10PrefixLookup[groupIcd10Code as keyof typeof icd10PrefixLookup];

    // Extract display name from first non-empty coding (will eventually be replaced with getTitle(coding))
    const firstDisplay = mostRecentCondition.code?.coding?.find(coding => coding.display)?.display;
    const title = groupIcd10Display || firstDisplay || mostRecentCondition.code?.text || "-";

    // Sort conditions with newest first for display
    const sortedConditions = [...series.resources].reverse();

    const hasEncounterDiagnosis = sortedConditions.some(
      condition => getConditionCategory(condition) === "Encounter Diagnosis"
    );

    const hccCode = getConditionHccCode(mostRecentCondition);

    // Collect all codings (ICD-10, SNOMED, HCC, etc.)
    const codings: Coding[] = [{ system: ICD_10_SYSTEM, code: groupIcd10Code }];

    // Add HCC codings from extensions
    const hccCodeableConcepts = getHccCoditionsForSidePanel(mostRecentCondition);
    hccCodeableConcepts.forEach(codeableConcept => {
      codeableConcept.coding?.forEach(coding => {
        if (coding.system && coding.code) {
          codings.push(coding);
        }
      });
    });

    return {
      id: mostRecentCondition.id ?? "-",
      condition: title,
      hasEncounterDiagnosis,
      code: groupIcd10Code + hccCode,
      codings,
      firstSeen: getEarliestSeen(sortedConditions, patientDob),
      lastSeen: getLatestSeen(sortedConditions, patientDob),
      originalData: firstCondition,
      isChronic: isChronicCondition(mostRecentCondition),
      isHcc: isHccCondition(mostRecentCondition, hccCode),
      conditions: sortedConditions,
      patientDob,
    };
  });
}

function getEarliestSeen(
  conditions: Smart<Condition>[] | undefined,
  patientDob: string | undefined
): string {
  if (!conditions || conditions.length === 0) return "-";

  const dates = conditions
    .map(condition => getOnsetTime(condition, patientDob))
    .filter((date): date is string => !!date && date !== patientDob);

  if (dates.length === 0) return "-";

  return dates.reduce((earliest, current) => {
    return new Date(current) < new Date(earliest) ? current : earliest;
  });
}

function getLatestSeen(
  conditions: Smart<Condition>[] | undefined,
  patientDob: string | undefined
): string {
  if (!conditions || conditions.length === 0) return "-";

  const dates = conditions
    .map(condition => getEndTime(condition, patientDob))
    .filter((date): date is string => !!date && date !== patientDob);

  if (dates.length === 0) return "-";

  return dates.reduce((latest, current) => {
    return new Date(current) > new Date(latest) ? current : latest;
  });
}
